import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AssetDiagnosticsService,
  AssetDiagnosticsUnavailableError,
} from '~/app/services/asset/asset-diagnostics.service';
import type { DiagnosticsHistoryRow } from '../types';
import {
  DIAGNOSTICS_HISTORY_PAGE_SIZE,
  diagnosticsHistoryHasNonTerminal,
} from '../utils';
import { useDiagnosticsHistoryColumns } from './useDiagnosticsHistoryColumns';

const POLL_MS = 5_000;

export function diagnosticsHistoryQueryKey(
  assetId: string,
  page: number,
  limit: number,
) {
  return ['asset-ocpp-diagnostics', assetId, page, limit] as const;
}

export function useDiagnosticsHistoryPanel(
  organizationId: string,
  assetId: string,
) {
  const { t } = useTranslation('assets');
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [unavailableById, setUnavailableById] = useState<
    Record<string, string>
  >({});

  const limit = DIAGNOSTICS_HISTORY_PAGE_SIZE;

  const query = useQuery({
    queryKey: diagnosticsHistoryQueryKey(assetId, page, limit),
    queryFn: () =>
      AssetDiagnosticsService.listByAssetId(organizationId, assetId, {
        page,
        limit,
      }),
    refetchInterval: (current) =>
      diagnosticsHistoryHasNonTerminal(current.state.data?.data ?? [])
        ? POLL_MS
        : false,
  });

  const downloadMutation = useMutation({
    mutationFn: (row: DiagnosticsHistoryRow) =>
      AssetDiagnosticsService.createDownloadUrl(
        organizationId,
        assetId,
        row.id,
      ),
    onMutate: (row) => {
      setDownloadingId(row.id);
      setUnavailableById((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    },
    onSuccess: (result) => {
      window.open(result.url, '_blank', 'noopener,noreferrer');
    },
    onError: (error, row) => {
      const messageKey =
        error instanceof AssetDiagnosticsUnavailableError
          ? 'detail.commands.diagnosticsHistory.fileNoLongerAvailable'
          : 'detail.commands.diagnosticsHistory.downloadError';
      setUnavailableById((prev) => ({
        ...prev,
        [row.id]: messageKey,
      }));
    },
    onSettled: () => {
      setDownloadingId(null);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (row: DiagnosticsHistoryRow) =>
      AssetDiagnosticsService.cancel(organizationId, assetId, row.id),
    onMutate: (row) => {
      setCancellingId(row.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['asset-ocpp-diagnostics', assetId],
      });
    },
    onError: () => {
      void message.error(t('detail.commands.diagnosticsHistory.cancelError'));
    },
    onSettled: () => {
      setCancellingId(null);
    },
  });

  const onDownload = useCallback(
    (row: DiagnosticsHistoryRow) => {
      downloadMutation.mutate(row);
    },
    [downloadMutation],
  );

  const onCancel = useCallback(
    (row: DiagnosticsHistoryRow) => cancelMutation.mutateAsync(row),
    [cancelMutation],
  );

  const columns = useDiagnosticsHistoryColumns({
    downloadingId,
    cancellingId,
    unavailableById,
    onDownload,
    onCancel,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['asset-ocpp-diagnostics', assetId],
    });
  }, [assetId, queryClient]);

  const onPageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  return {
    columns,
    rows: query.data?.data ?? [],
    total: query.data?.meta.total ?? 0,
    page,
    pageSize: limit,
    isLoading: query.isLoading,
    isError: query.isError,
    refresh,
    onPageChange,
  };
}
